import { createActionHandler, createReducer, OperationStatus } from '@pressreader/appstore';
import type { ArticleCollections } from '@pressreader/social-types';

import {
    loadArticleCollectionsBegan,
    loadArticleCollectionsCancelled,
    loadArticleCollectionsDone,
    loadArticleCollectionsFailed,
    removeArticleFromCollectionsBegan,
    removeArticleFromCollectionsDone,
    removeArticleFromCollectionsFailed,
    resetArticleCollectionsAction,
    upsertArticleCollectionsBegan,
    upsertArticleCollectionsDone,
    upsertArticleCollectionsFailed,
} from '../actions/article.collections';

import { updateArticleCollections, updateArticleStatus } from './article.collections.utils';

interface ArticleCollectionsState {
    status: OperationStatus;
    collections: ArticleCollections | undefined;
}

interface State {
    /**
     * A map of article id to article collections
     */
    articleCollections: Record<string, ArticleCollectionsState>;
}

const initialState: State = {
    articleCollections: {},
};

const reducer = createReducer(
    initialState,
    createActionHandler(loadArticleCollectionsBegan, (state, { payload }) =>
        updateArticleStatus(state, {
            articleId: payload.articleId,
            status: OperationStatus.Pending,
        }),
    ),
    createActionHandler(loadArticleCollectionsDone, (state, { payload }) =>
        updateArticleCollections(state, {
            articleId: payload.articleId,
            collections: payload.collections,
        }),
    ),
    createActionHandler(loadArticleCollectionsFailed, (state, { payload }) =>
        updateArticleStatus(state, { articleId: payload.articleId, status: OperationStatus.Failed }),
    ),
    createActionHandler(loadArticleCollectionsCancelled, (state, { payload }) =>
        updateArticleStatus(state, { articleId: payload.articleId, status: OperationStatus.None }),
    ),
    createActionHandler(upsertArticleCollectionsBegan, (state, { payload }) =>
        updateArticleStatus(state, {
            articleId: payload.articleId,
            status: OperationStatus.Pending,
        }),
    ),
    createActionHandler(upsertArticleCollectionsDone, (state, { payload }) =>
        updateArticleCollections(state, {
            articleId: payload.articleId,
            collections: payload.articleCollections,
        }),
    ),
    createActionHandler(upsertArticleCollectionsFailed, (state, { payload }) =>
        updateArticleStatus(state, {
            articleId: payload.articleId,
            status: OperationStatus.Failed,
        }),
    ),
    createActionHandler(resetArticleCollectionsAction, (state, { payload }) => ({
        ...state,
        articleCollections: {
            ...state.articleCollections,
            [payload.articleId]: {
                ...state.articleCollections[payload.articleId],
                status: OperationStatus.Failed,
                collections: undefined,
            },
        },
    })),
    createActionHandler(removeArticleFromCollectionsBegan, (state, { payload }) => ({
        ...state,
        articleCollections: {
            ...state.articleCollections,
            [payload.articleId]: {
                ...state.articleCollections[payload.articleId],
                status: OperationStatus.Pending,
            },
        },
    })),
    createActionHandler(removeArticleFromCollectionsDone, (state, { payload }) => ({
        ...state,
        articleCollections: {
            ...state.articleCollections,
            [payload.articleId]: {
                ...state.articleCollections[payload.articleId],
                status: OperationStatus.Done,
                collections: undefined,
            },
        },
    })),
    createActionHandler(removeArticleFromCollectionsFailed, (state, { payload }) => ({
        ...state,
        articleCollections: {
            ...state.articleCollections,
            [payload.articleId]: {
                ...state.articleCollections[payload.articleId],
                status: OperationStatus.Failed,
            },
        },
    })),
);

export type { State, ArticleCollectionsState };
export { reducer };
